/**
 * ATTACHED HAIR SOFTENS — ADR-039 T2a (Allen, 2026-10-01): hair that LIES ON
 * the head, neck and shoulders gets softening, not free motion. It shakes a
 * little and its highlights move, but it never lifts off the body it rests on
 * and never enters the skull.
 *
 * ONE responsibility: given the body (bind-world arrays, the paint the run has
 * reached), a person's CONFIRMED hair region (the zone's faces) and its
 * confirmed skull, return three per-vertex answers. No bones are grafted here
 * and no scene is touched: the app's pass (`app/hair/hair-soften-run`) seats
 * the answers through the run's doors.
 *
 *  1. WEIGHTS — Harry's own (`hairWeights`: the Head subtree only), FEATHERED
 *     into the paint at the region's border so no seam opens when a shoulder
 *     moves. Per welded position, t = min(1, dBorder / band), dBorder the
 *     distance to the nearest RING position (a region position any non-hair
 *     face also uses): the ring keeps the inherited paint (t = 0), the inside
 *     is Harry's (t = 1), and in between the two blend linearly. This answers
 *     §11's open question for attached hair: the nape hair that rests on the
 *     body keeps the body's weights at the border (Head + whatever the
 *     neighbouring skin wears), Head alone inside. The blend can name up to
 *     eight bones; the four strongest survive (the eviction is counted) and
 *     the largest takes the float32 remainder, so the sum is exact.
 *     Outside the region NOTHING changes — the arrays are copies, and only the
 *     region's vertices are written.
 *  2. THE LEAF'S SHARE — the hair leaf (`HairJiggle`, a child of the Head,
 *     pivoting at the skull's CENTRE so a swing slides hair round the skull,
 *     never into it) takes a share of each vertex's HEAD weight:
 *       share = peak · ramp(dScalp) · ramp(dBody) · t · (not face)
 *     0 within the scalp shell, rising to `peak` a ramp beyond it; 0 within the
 *     contact distance of the BODY it rests on (flesh outside the region that
 *     the Head subtree does not own — the neck, shoulders, back), rising over
 *     one more contact distance; 0 at the ring (× t, so the border stays put
 *     relative to the skin beyond it); 0 in the FACE.
 *  3. HELD — the vertices the surface modes pin (φ = 0, ADR-038 §14): the
 *     ring ∪ the scalp shell ∪ the body contact ∪ the face. The same sets the
 *     leaf's share is zero on, so the ripple and the bounce agree on what
 *     never moves.
 *
 * THE FACE is flesh in front of the skull's centre, between the chin and the
 * brow, within {@link HAIR_FACE_BREADTH} of the skull's half-breadth: T1's face
 * mask (`measureHair`) widened by 15% so the cheeks' bulge past the inscribed
 * skull counts. The detector's region never holds the mask itself, but its
 * margin, a dragged skull or a reshaped zone can reach a cheek, and a jiggling
 * cheek is the one failure this treatment must never show: such vertices are
 * held and get no share. The count is reported. (A first cut without the
 * chin and breadth limits held the front locks of every bob — 21–24% of the
 * region on the four specimens — and they are exactly what should soften.)
 *
 * Every distance is in SKULL RADII R (the cube root of the confirmed skull's
 * semi-axes' product), so the thresholds scale with the head, whatever the
 * units — the numbers are ADR-039 §13's, measured on the attached specimens.
 *
 * Depends on: hair/skull (radial, the declaration), hair/hair-weights.
 * Depended on by: the app's hair pass and its door test.
 */
import { radial, type Ellipsoid, type SkullDeclarationShape } from './skull.js';
import { hairWeights, type HairWeightsResult } from './hair-weights.js';

/**
 * The border feather's band: Harry's weights in full this far inside the ring
 * (skull radii; ≈ 5 cm on the four attached specimens, R 50–55 mm). Measured
 * 2026-10-01 (ADR-039 §13): the steepest edge the feather adds falls 0.37–0.69
 * → 0.18–0.36 from 0.5 R to 1 R (Harry's weights unfeathered: 0.90), level with
 * the paint's own border edges (0.22–0.81); 1.5 R buys little more and keeps
 * foreign weight in nearly the whole of a tight cap.
 */
export const HAIR_FEATHER_BAND_R = 1;
/**
 * Hair within this of the skull's surface is the scalp: held, no leaf share
 * (skull radii; ≈ 13 mm). The region starts 0.15 R out (T1's margin); the tight
 * caps' first quartile lies at 0.32–0.33 R, so this holds the 4–19% that hug it.
 */
export const HAIR_SCALP_SHELL_R = 0.1; // Allen 2026-10-01 field read: "soften the hair more even near the skull" (was 0.25)
/** The leaf's share rises from 0 at the scalp shell to its peak over this (skull radii). */
export const HAIR_SHARE_RAMP_R = 0.15; // was 0.35 — the share reaches its peak close to the scalp
/**
 * Hair within this of the body it rests on is in contact: held, no leaf share;
 * the share recovers over one more (skull radii; ≈ 16 mm, so full share only
 * from ≈ 3 cm off the shoulder — the leaf's 6° cone moves the far hair < 8 mm).
 */
export const HAIR_CONTACT_R = 0.15; // was 0.3 — only hair actually on the body is held
/** The leaf's largest share of a vertex's Head weight — softening, not free motion. */
export const HAIR_PEAK_SHARE = 0.85; // was 0.5 — "take it too far and come back" (Allen, 2026-10-01)
/** The face box's half-breadth, as a multiple of the skull's: T1's face mask plus the cheeks' bulge. */
export const HAIR_FACE_BREADTH = 1.15;

/**
 * The leaf's droop below horizontal (degrees). Allen's field read 2026-10-01: the hair jiggled but
 * never bounced up and down or swayed side to side. The leaf used to point at the hair's centroid
 * — DOWN, for hair that hangs — and J2's drive bounces about the leaf's lateral axis (a downward tip
 * only swings fore–aft) and sways about the body's up (a downward leaf only twists). Aimed BACK
 * and nearly level, the same drive lifts and drops the hair behind the skull (bounce) and swings
 * it sideways (sway); the droop keeps a little of the fore–aft swing.
 */
export const HAIR_LEAF_DROOP_DEG = 20;

/**
 * The leaf's tip: from the skull centre, as far as the hair's share-weighted centroid, aimed along
 * the centroid's level offset (behind the skull for a bob) — or straight back when the hair sits
 * level all round (the offset under a fifth of the reach) — then drooped {@link HAIR_LEAF_DROOP_DEG}.
 */
function aimLeaf(skull: SkullDeclarationShape, centroid: { readonly x: number; readonly y: number; readonly z: number }): { x: number; y: number; z: number } {
  const c = skull.centre;
  const dx = centroid.x - c.x, dy = centroid.y - c.y, dz = centroid.z - c.z;
  const reach = Math.hypot(dx, dy, dz);
  const level = Math.hypot(dx, dz);
  const yaw = (skull.yawDeg * Math.PI) / 180;
  const [hx, hz] = level >= 0.2 * reach ? [dx / level, dz / level] : [-Math.sin(yaw), -Math.cos(yaw)];
  const droop = (HAIR_LEAF_DROOP_DEG * Math.PI) / 180;
  return { x: c.x + reach * Math.cos(droop) * hx, y: c.y - reach * Math.sin(droop), z: c.z + reach * Math.cos(droop) * hz };
}

/** The dials above, overridable per call (the measurement probes; a future viewport dial). */
export interface HairSoftenTuning {
  readonly featherBandR: number;
  readonly scalpShellR: number;
  readonly shareRampR: number;
  readonly contactR: number;
  readonly peakShare: number;
}
export const HAIR_SOFTEN_TUNING: HairSoftenTuning = {
  featherBandR: HAIR_FEATHER_BAND_R,
  scalpShellR: HAIR_SCALP_SHELL_R,
  shareRampR: HAIR_SHARE_RAMP_R,
  contactR: HAIR_CONTACT_R,
  peakShare: HAIR_PEAK_SHARE,
};

export interface HairSoftenInput {
  /** Bind-world positions, xyz interleaved. */
  readonly positions: ArrayLike<number>;
  readonly indices: ArrayLike<number>;
  /** Four influences per vertex: the paint the run has reached (the pass's input). */
  readonly skin: { readonly index: ArrayLike<number>; readonly weight: ArrayLike<number> };
  /** Skin-order bones (names, parents). */
  readonly bones: readonly { readonly name: string; readonly parent: string | null }[];
  readonly headBone: string;
  /** The confirmed region: the hair zone's faces. */
  readonly regionFaces: readonly number[];
  /** The confirmed skull, bind-world (the Document's declaration). */
  readonly skull: SkullDeclarationShape;
  /** The face's heights, bind-world: the brow (eye height + 0.35 F, T1's face mask) and the chin. */
  readonly face: { readonly brow: number; readonly chin: number };
  readonly tuning?: Partial<HairSoftenTuning>;
}

export interface HairSoftenSeam {
  /** Max total variation ½Σ|wa − wb| (0 = the same paint, 1 = disjoint) over edges crossing the region's border (ring ↔ outside). */
  readonly crossing: number;
  /** Max INCREASE of an edge's total variation over the inherited paint's, over every edge touching the region — how much steeper the paint got. */
  readonly added: number;
}

export interface HairSoftenResult {
  /** Full copies of the skin; only the region's vertices differ from the input. */
  readonly index: Uint32Array;
  readonly weight: Float32Array;
  /** The region's raw vertices (twins included), ascending — the arrays below are aligned with it. */
  readonly vertices: readonly number[];
  /** Per region vertex: the feather t (0 at the ring → 1 a band inside). */
  readonly feather: Float64Array;
  /** Per region vertex: the leaf's share of the vertex's Head weight. */
  readonly share: Float64Array;
  /** Per region vertex: 1 = held by the surface modes (φ = 0). */
  readonly held: Uint8Array;
  /** Welded counts: the region, and why held (a position can be held for several reasons). */
  readonly welded: number;
  readonly heldBy: { readonly ring: number; readonly scalp: number; readonly contact: number; readonly face: number; readonly total: number };
  /** Welded region positions in the face (in front of the skull's centre, chin to brow, within the face's breadth). */
  readonly face: number;
  /** Region vertices whose weights changed, and influences evicted by the blend's four-slot cap. */
  readonly reweighted: number;
  readonly evicted: number;
  /** Region vertices still carrying a bone outside the Head subtree after the feather (the border's blend). */
  readonly foreignAtBorder: number;
  /** The foreign (non-Head-subtree) weight the feather kept, as a share of what the region carried (0 = all stripped). */
  readonly foreignKept: number;
  /** Harry's unfeathered weights, as `hairWeights` reports them (what was stripped, from how many). */
  readonly harry: Pick<HairWeightsResult, 'changed' | 'toHead' | 'stripped'>;
  /** The seam metric: the inherited paint, Harry's weights unfeathered, and feathered. */
  readonly seam: { readonly inherited: HairSoftenSeam; readonly hard: HairSoftenSeam; readonly feathered: HairSoftenSeam };
  /** The leaf: root = the skull's centre, tip aimed back and nearly level at the hair's reach ({@link HAIR_LEAF_DROOP_DEG}; bind-world). */
  readonly root: { readonly x: number; readonly y: number; readonly z: number };
  readonly tip: { readonly x: number; readonly y: number; readonly z: number };
  /** Region vertices the leaf takes a share of (raw, twins included), and welded positions. */
  readonly weighted: number;
  readonly weightedWelded: number;
  /** The skull radius R (metres) every threshold was scaled by. */
  readonly skullR: number;
}

/** The softening cannot be built on this region: said, never guessed (ADR-039 §13 fail-loud). */
export class HairSoftenRefusal extends Error {}

const smooth = (x: number): number => {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
};

/** Per `from` position, the distance to the nearest `to` position when it is within `d`, else Infinity. A uniform grid of cell `d`. */
function nearestWithin(P: ArrayLike<number>, from: readonly number[], to: readonly number[], d: number): Float64Array {
  const out = new Float64Array(from.length).fill(Infinity);
  if (to.length === 0 || !(d > 0)) return out;
  const cell = (x: number): number => Math.floor(x / d);
  const key = (a: number, b: number, c: number): string => `${a},${b},${c}`;
  const grid = new Map<string, number[]>();
  for (const j of to) {
    const k = key(cell(P[j * 3]!), cell(P[j * 3 + 1]!), cell(P[j * 3 + 2]!));
    const bucket = grid.get(k);
    if (bucket === undefined) grid.set(k, [j]);
    else bucket.push(j);
  }
  from.forEach((i, q) => {
    const x = P[i * 3]!;
    const y = P[i * 3 + 1]!;
    const z = P[i * 3 + 2]!;
    const cx = cell(x);
    const cy = cell(y);
    const cz = cell(z);
    let best = Infinity;
    for (let a = -1; a <= 1; a++)
      for (let b = -1; b <= 1; b++)
        for (let c = -1; c <= 1; c++) {
          const bucket = grid.get(key(cx + a, cy + b, cz + c));
          if (bucket === undefined) continue;
          for (const j of bucket) {
            const dd = Math.hypot(P[j * 3]! - x, P[j * 3 + 1]! - y, P[j * 3 + 2]! - z);
            if (dd < best) best = dd;
          }
        }
    out[q] = best <= d ? best : Infinity;
  });
  return out;
}

/** The skull in its own frame: (left, up, forward) about its centre, for `radial`. */
function skullFrame(d: SkullDeclarationShape): { e: Ellipsoid; local: (x: number, y: number, z: number) => [number, number, number] } {
  const yaw = (d.yawDeg * Math.PI) / 180;
  const fx = Math.sin(yaw);
  const fz = Math.cos(yaw);
  const c = d.centre;
  // u = left = (fz, 0, −fx), v = up, w = forward = (fx, 0, fz) — skull.ts's head frame
  const local = (x: number, y: number, z: number): [number, number, number] => {
    const dx = x - c.x;
    const dz = z - c.z;
    return [dx * fz - dz * fx, y - c.y, dx * fx + dz * fz];
  };
  return { e: { c: [0, 0, 0], r: [d.semiAxes.x, d.semiAxes.y, d.semiAxes.z] }, local };
}

/** Exact float32 partition of unity: the largest slot takes the remainder (hairWeights' rule). */
function writeExact(index: Uint32Array, weight: Float32Array, v: number, kept: readonly (readonly [number, number])[]): void {
  const total = kept.reduce((s, [, w]) => s + w, 0);
  let largest = 0;
  for (let k = 0; k < 4; k++) {
    const e = kept[k];
    index[v * 4 + k] = e === undefined ? 0 : e[0];
    weight[v * 4 + k] = e === undefined ? 0 : Math.fround(e[1] / total);
    if (e !== undefined && weight[v * 4 + k]! > weight[v * 4 + largest]!) largest = k;
  }
  let sum = 0;
  for (let k = 0; k < 4; k++) if (k !== largest) sum = Math.fround(sum + weight[v * 4 + k]!);
  weight[v * 4 + largest] = Math.fround(1 - sum);
}

export function softenAttachedHair(input: HairSoftenInput): HairSoftenResult {
  const P = input.positions;
  const I = input.indices;
  const n = Math.floor(P.length / 3);
  const faceCount = Math.floor(I.length / 3);
  const { e, local } = skullFrame(input.skull);
  const tune = { ...HAIR_SOFTEN_TUNING, ...input.tuning };
  const R = Math.cbrt(e.r[0] * e.r[1] * e.r[2]);
  if (!(R > 0)) throw new HairSoftenRefusal('the confirmed skull has no size — re-confirm it');

  // ── the region, welded ──
  const inFace = new Uint8Array(faceCount);
  const inRegion = new Uint8Array(n);
  for (const f of input.regionFaces) {
    if (!(f >= 0 && f < faceCount)) throw new HairSoftenRefusal(`the hair zone names face ${f}, which this body does not have — confirm the hair again`);
    inFace[f] = 1;
    for (let c = 0; c < 3; c++) inRegion[I[f * 3 + c]!] = 1;
  }
  const vertices: number[] = [];
  for (let v = 0; v < n; v++) if (inRegion[v] === 1) vertices.push(v);
  if (vertices.length === 0) throw new HairSoftenRefusal('the hair zone has no faces on this body — confirm the hair again');
  const weld = new Int32Array(n);
  const keyOf = new Map<string, number>();
  const reps: number[] = [];
  for (let v = 0; v < n; v++) {
    const k = `${Math.round(P[v * 3]! * 1e5)},${Math.round(P[v * 3 + 1]! * 1e5)},${Math.round(P[v * 3 + 2]! * 1e5)}`;
    const at = keyOf.get(k);
    if (at === undefined) {
      keyOf.set(k, v);
      weld[v] = v;
      reps.push(v);
    } else weld[v] = at;
  }
  // a position is in the region when any twin is; the RING when any non-region face uses it too
  const posIn = new Uint8Array(n);
  for (const v of vertices) posIn[weld[v]!] = 1;
  const ring = new Uint8Array(n);
  for (let f = 0; f < faceCount; f++) {
    if (inFace[f] === 1) continue;
    for (let c = 0; c < 3; c++) {
      const p = weld[I[f * 3 + c]!]!;
      if (posIn[p] === 1) ring[p] = 1;
    }
  }
  const regionReps = reps.filter((p) => posIn[p] === 1);
  const ringReps = regionReps.filter((p) => ring[p] === 1);

  // ── who owns the body: the Head subtree, by the inherited paint's dominant bone ──
  const head = input.bones.findIndex((b) => b.name === input.headBone);
  if (head < 0) throw new HairSoftenRefusal(`no bone "${input.headBone}" in the skin`);
  const byName = new Map(input.bones.map((b, i) => [b.name, i]));
  const sub = new Set<number>([head]);
  for (let grew = true; grew; ) {
    grew = false;
    input.bones.forEach((b, i) => {
      if (!sub.has(i) && b.parent !== null && sub.has(byName.get(b.parent) ?? -1)) {
        sub.add(i);
        grew = true;
      }
    });
  }
  const { index: SI, weight: SW } = input.skin;
  const dominant = (v: number): number => {
    let best = -1;
    let bw = -1;
    for (let k = 0; k < 4; k++) {
      const w = SW[v * 4 + k]!;
      if (w > bw) {
        bw = w;
        best = SI[v * 4 + k]!;
      }
    }
    return best;
  };
  const body = reps.filter((p) => posIn[p] === 0 && !sub.has(dominant(p)));

  // ── per welded region position: the scalp, the border, the body, the face ──
  const band = tune.featherBandR * R;
  const contact = tune.contactR * R;
  const shell = tune.scalpShellR * R;
  const dBorder = nearestWithin(P, regionReps, ringReps, band);
  const dBody = nearestWithin(P, regionReps, body, 2 * contact);
  const t = new Float64Array(n);
  const s = new Float64Array(n);
  const hold = new Uint8Array(n);
  let heldRing = 0;
  let heldScalp = 0;
  let heldContact = 0;
  let heldFace = 0;
  let heldTotal = 0;
  let face = 0;
  let sw = 0;
  let tx = 0;
  let ty = 0;
  let tz = 0;
  regionReps.forEach((p, q) => {
    const x = P[p * 3]!;
    const y = P[p * 3 + 1]!;
    const z = P[p * 3 + 2]!;
    const [u, v, w] = local(x, y, z);
    const dScalp = radial(e, u, v, w);
    const inFaceZone = w > 0 && y < input.face.brow && y > input.face.chin && Math.abs(u) < HAIR_FACE_BREADTH * e.r[0];
    const tt = ring[p] === 1 ? 0 : Math.min(1, dBorder[q]! / band);
    const atScalp = dScalp < shell;
    const atBody = dBody[q]! < contact;
    t[p] = tt;
    if (inFaceZone) face++;
    const share = inFaceZone
      ? 0
      : tune.peakShare * smooth((dScalp - shell) / (tune.shareRampR * R)) * smooth((dBody[q]! - contact) / contact) * tt;
    s[p] = share;
    if (ring[p] === 1) heldRing++;
    if (atScalp) heldScalp++;
    if (atBody) heldContact++;
    if (inFaceZone) heldFace++;
    if (ring[p] === 1 || atScalp || atBody || inFaceZone) {
      hold[p] = 1;
      heldTotal++;
    }
    if (share > 0) {
      sw += share;
      tx += share * x;
      ty += share * y;
      tz += share * z;
    }
  });
  if (!(sw > 0)) {
    throw new HairSoftenRefusal(
      `no hair stands more than ${tune.scalpShellR} skull radii off the scalp and ${tune.contactR} off the body — nothing to soften (the skull may sit too large: adjust it)`,
    );
  }

  // ── Harry's weights, then the feather ──
  const harry = hairWeights({ skin: input.skin, bones: input.bones, headBone: input.headBone, vertices });
  const index = Uint32Array.from(SI as ArrayLike<number>);
  const weight = Float32Array.from(SW as ArrayLike<number>);
  let reweighted = 0;
  let evicted = 0;
  let foreignAtBorder = 0;
  let foreignBefore = 0;
  let foreignAfter = 0;
  const feather = new Float64Array(vertices.length);
  const share = new Float64Array(vertices.length);
  const held = new Uint8Array(vertices.length);
  vertices.forEach((v, q) => {
    const p = weld[v]!;
    const tt = t[p]!;
    feather[q] = tt;
    share[q] = s[p]!;
    held[q] = hold[p]!;
    // Harry's weights differ from the inherited paint only where it names a bone outside the Head subtree.
    let foreignHere = false;
    for (let k = 0; k < 4; k++) if (SW[v * 4 + k]! > 0 && !sub.has(SI[v * 4 + k]!)) foreignHere = true;
    if (tt > 0 && foreignHere) {
      const merged = new Map<number, number>();
      for (let k = 0; k < 4; k++) {
        const a = SW[v * 4 + k]!;
        if (a > 0) merged.set(SI[v * 4 + k]!, (merged.get(SI[v * 4 + k]!) ?? 0) + (1 - tt) * a);
        const b = harry.weight[v * 4 + k]!;
        if (b > 0) merged.set(harry.index[v * 4 + k]!, (merged.get(harry.index[v * 4 + k]!) ?? 0) + tt * b);
      }
      for (const [bone, w] of merged) if (!(w > 0)) merged.delete(bone);
      const sorted = [...merged].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
      evicted += Math.max(0, sorted.length - 4);
      writeExact(index, weight, v, sorted.slice(0, 4));
      reweighted++;
    }
    let foreign = false;
    for (let k = 0; k < 4; k++) {
      if (SW[v * 4 + k]! > 0 && !sub.has(SI[v * 4 + k]!)) foreignBefore += SW[v * 4 + k]!;
      if (weight[v * 4 + k]! > 0 && !sub.has(index[v * 4 + k]!)) {
        foreignAfter += weight[v * 4 + k]!;
        foreign = true;
      }
    }
    if (foreign) foreignAtBorder++;
  });

  // ── the seam metric: total variation across edges, three paints ──
  const tv = (Ix: ArrayLike<number>, Wx: ArrayLike<number>, a: number, b: number): number => {
    const m = new Map<number, number>();
    for (let k = 0; k < 4; k++) {
      m.set(Ix[a * 4 + k]!, (m.get(Ix[a * 4 + k]!) ?? 0) + Wx[a * 4 + k]!);
      m.set(Ix[b * 4 + k]!, (m.get(Ix[b * 4 + k]!) ?? 0) - Wx[b * 4 + k]!);
    }
    let d = 0;
    for (const x of m.values()) d += Math.abs(x);
    return d / 2;
  };
  const seamOf = (Ix: ArrayLike<number>, Wx: ArrayLike<number>): HairSoftenSeam => {
    let crossing = 0;
    let added = 0;
    for (let f = 0; f < faceCount; f++) {
      for (let c = 0; c < 3; c++) {
        const a = I[f * 3 + c]!;
        const b = I[f * 3 + ((c + 1) % 3)]!;
        const ra = inRegion[a] === 1;
        const rb = inRegion[b] === 1;
        if (!ra && !rb) continue;
        const d = tv(Ix, Wx, a, b);
        added = Math.max(added, d - tv(SI, SW, a, b));
        if (ra !== rb) crossing = Math.max(crossing, d);
      }
    }
    return { crossing, added };
  };

  const tip = aimLeaf(input.skull, { x: tx / sw, y: ty / sw, z: tz / sw });
  return {
    index,
    weight,
    vertices,
    feather,
    share,
    held,
    welded: regionReps.length,
    heldBy: { ring: heldRing, scalp: heldScalp, contact: heldContact, face: heldFace, total: heldTotal },
    face,
    reweighted,
    evicted,
    foreignAtBorder,
    foreignKept: foreignBefore > 0 ? foreignAfter / foreignBefore : 0,
    harry: { changed: harry.changed, toHead: harry.toHead, stripped: harry.stripped },
    seam: { inherited: seamOf(SI, SW), hard: seamOf(harry.index, harry.weight), feathered: seamOf(index, weight) },
    root: { ...input.skull.centre },
    tip,
    weighted: share.reduce((c, x) => c + (x > 0 ? 1 : 0), 0),
    weightedWelded: regionReps.reduce((c, p) => c + (s[p]! > 0 ? 1 : 0), 0),
    skullR: R,
  };
}
